/**
 * URL-backed filter state for the Ads Manager pages.
 *
 * Filters live in the URL so:
 *   - Browser back/forward naturally restores them
 *   - Links are shareable (e.g. paste "/ads?preset=30d&app=4" into Slack)
 *   - Navigating Campaigns → Ad Groups → Keywords preserves period & app
 *
 * URL params: `preset`, `from`, `to`, `app`. The picker shape mirrors
 * Overview's two-column "from + to" calendar (DateRangePicker), so a
 * preset-only URL (`?preset=14d`) restores cleanly and a custom-range
 * URL (`?preset=custom&from=2026-04-01&to=2026-04-15`) round-trips
 * verbatim.
 *
 * Usage:
 *
 *   const [filters, setFilters] = useAdsFilters();
 *   // filters = { preset: "7d", range: {from, to}, appId: "all" }
 *   setFilters({ preset: "30d", range: rangeFromPreset("30d", new Date()) });
 *   setFilters({ appId: 4 });
 */

import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  type DateRange,
  type RangeMode,
  type RangePreset,
  rangeFromPreset,
  rangeToApi,
} from "@/lib/date-range";
import type { StatsFilters } from "./types";

export type AdsFilters = {
  preset: RangePreset;
  range: DateRange;
  /** Rolling vs static is mostly a hint to the date picker — for the
   *  Ads Manager URL state we honour `mode=static` by parking the
   *  exact `from` / `to` dates regardless of preset, so that pasting
   *  the URL re-opens the same calendar window. With `mode=rolling`
   *  (the default) named presets re-derive their range from `today`. */
  mode: RangeMode;
  appId: "all" | number;
};

export type PartialAdsFilters = Partial<AdsFilters>;

const DEFAULT_PRESET: RangePreset = "7d";
const DEFAULT_MODE: RangeMode = "rolling";

const VALID_PRESETS: readonly RangePreset[] = [
  "today",
  "yesterday",
  "7d",
  "14d",
  "this_week",
  "last_week",
  "this_mo",
  "last_mo",
  "30d",
  "3mo",
  "4w",
  "12w",
  "ytd",
  "custom",
];

function parseYmd(s: string | null): Date | null {
  if (!s) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
}

function toYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

export function useAdsFilters(): [AdsFilters, (next: PartialAdsFilters) => void] {
  const [params, setParams] = useSearchParams();

  const filters = useMemo<AdsFilters>(() => {
    const rawPreset = params.get("preset");
    const preset: RangePreset = (VALID_PRESETS as readonly string[]).includes(
      rawPreset ?? ""
    )
      ? (rawPreset as RangePreset)
      : DEFAULT_PRESET;

    const rawMode = params.get("mode");
    const mode: RangeMode = rawMode === "static" ? "static" : DEFAULT_MODE;

    // For preset=custom OR mode=static the URL `from`/`to` are
    // authoritative — `static` pins exact calendar dates regardless of
    // the preset name. For any named preset under `rolling` the range
    // is derived freshly each render, so `?preset=7d` opened tomorrow
    // still means "yesterday's last 7 days".
    let range: DateRange;
    const useExact = preset === "custom" || mode === "static";
    if (useExact) {
      const from = parseYmd(params.get("from"));
      const to = parseYmd(params.get("to"));
      range =
        from && to && from <= to
          ? { from, to }
          : rangeFromPreset(preset === "custom" ? DEFAULT_PRESET : preset, new Date());
    } else {
      range = rangeFromPreset(preset, new Date());
    }

    const rawApp = params.get("app");
    const appId: "all" | number =
      !rawApp || rawApp === "all" ? "all" : Number(rawApp);

    return {
      preset,
      range,
      mode,
      appId: Number.isNaN(appId as number) ? "all" : appId,
    };
  }, [params]);

  const update = useCallback(
    (next: PartialAdsFilters) => {
      setParams(
        (prev) => {
          const copy = new URLSearchParams(prev);
          if (next.preset !== undefined) {
            if (next.preset === DEFAULT_PRESET) copy.delete("preset");
            else copy.set("preset", next.preset);
          }
          if (next.mode !== undefined) {
            if (next.mode === DEFAULT_MODE) copy.delete("mode");
            else copy.set("mode", next.mode);
          }
          if (next.range !== undefined) {
            // We persist `from`/`to` whenever the effective view is
            // calendar-locked: preset=custom OR mode=static. Named
            // rolling presets re-derive the range from today, so the
            // URL stays compact.
            const effectivePreset = next.preset ?? filters.preset;
            const effectiveMode = next.mode ?? filters.mode;
            const lockExact = effectivePreset === "custom" || effectiveMode === "static";
            if (lockExact) {
              copy.set("from", toYmd(next.range.from));
              copy.set("to", toYmd(next.range.to));
            } else {
              copy.delete("from");
              copy.delete("to");
            }
          } else if (next.preset !== undefined && next.preset !== "custom" &&
                     (next.mode ?? filters.mode) !== "static") {
            // Preset changed to a rolling, non-custom value — clear
            // any stale custom from/to params left behind.
            copy.delete("from");
            copy.delete("to");
          }
          if (next.appId !== undefined) {
            if (next.appId === "all") copy.delete("app");
            else copy.set("app", String(next.appId));
          }
          return copy;
        },
        { replace: false }
      );
    },
    [setParams, filters.preset, filters.mode]
  );

  return [filters, update];
}

/** Resolve UI filters → backend StatsFilters shape. */
export function adsFiltersToStatsFilters(f: AdsFilters): StatsFilters {
  const { date_from, date_to } = rangeToApi(f.range);
  return {
    date_from,
    date_to,
    app_ids: f.appId === "all" ? null : [f.appId],
  };
}
